/**
 * Easing curves as CSS timing functions. Physical curves (springs, bounces, elastic) are sampled
 * into CSS `linear(...)`, which Chromium and every current browser support, so the same string
 * drives the editor preview, HTML5 banners and the video renderer without a JS animation library.
 */

import type { Label } from './presets';

function sampled(fn: (t: number) => number, points = 48): string {
  const values: string[] = [];
  for (let i = 0; i <= points; i++) {
    const v = fn(i / points);
    values.push(String(Math.round(v * 10000) / 10000));
  }
  values[0] = '0';
  values[points] = '1';
  return `linear(${values.join(', ')})`;
}

/**
 * Damped spring from 0 to 1 over normalised time. `stiffness` and `damping` follow the usual
 * mass-spring conventions (mass 1); the curve is time-scaled so the spring has settled at t = 1.
 */
export function springCurve(stiffness = 180, damping = 14): (t: number) => number {
  const k = Math.max(1, stiffness);
  const c = Math.max(0.1, damping);
  const w0 = Math.sqrt(k);
  const zeta = c / (2 * w0);
  // Settling time to ~0.1% of the amplitude
  const settle = Math.min(10, Math.max(0.2, 6.9 / (zeta * w0)));
  return (t: number) => {
    const x = t * settle;
    if (zeta < 1) {
      const wd = w0 * Math.sqrt(1 - zeta * zeta);
      return (
        1 - Math.exp(-zeta * w0 * x) * (Math.cos(wd * x) + ((zeta * w0) / wd) * Math.sin(wd * x))
      );
    }
    return 1 - Math.exp(-w0 * x) * (1 + w0 * x);
  };
}

export function springEasing(stiffness = 180, damping = 14): string {
  return sampled(springCurve(stiffness, damping), 64);
}

function bounceOut(t: number): number {
  const n = 7.5625;
  const d = 2.75;
  if (t < 1 / d) return n * t * t;
  if (t < 2 / d) return n * (t -= 1.5 / d) * t + 0.75;
  if (t < 2.5 / d) return n * (t -= 2.25 / d) * t + 0.9375;
  return n * (t -= 2.625 / d) * t + 0.984375;
}

function elasticOut(t: number): number {
  if (t === 0 || t === 1) return t;
  return Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * ((2 * Math.PI) / 3)) + 1;
}

/** Named curves offered in the editor. */
export const EASINGS = {
  linear: 'linear',
  ease: 'ease',
  'ease-in': 'cubic-bezier(0.42, 0, 1, 1)',
  'ease-out': 'cubic-bezier(0, 0, 0.58, 1)',
  'ease-in-out': 'cubic-bezier(0.42, 0, 0.58, 1)',
  // Expressive curves used by most presets
  'out-quart': 'cubic-bezier(0.25, 1, 0.5, 1)',
  'out-expo': 'cubic-bezier(0.16, 1, 0.3, 1)',
  'in-expo': 'cubic-bezier(0.7, 0, 0.84, 0)',
  'in-out-expo': 'cubic-bezier(0.87, 0, 0.13, 1)',
  'in-quart': 'cubic-bezier(0.5, 0, 0.75, 0)',
  'in-out-quart': 'cubic-bezier(0.76, 0, 0.24, 1)',
  'out-back': 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  'in-back': 'cubic-bezier(0.36, 0, 0.66, -0.56)',
  'in-out-back': 'cubic-bezier(0.68, -0.6, 0.32, 1.6)',
  snappy: 'cubic-bezier(0.2, 0.9, 0.1, 1)',
  spring: springEasing(170, 12),
  'spring-soft': springEasing(100, 15),
  'spring-bouncy': springEasing(260, 9),
  bounce: sampled(bounceOut, 64),
  elastic: sampled(elasticOut, 64),
} as const;
export type EasingId = keyof typeof EASINGS;
export const EASING_IDS = Object.keys(EASINGS) as EasingId[];

const SPRING_RE = /^spring\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*\)$/;
const BEZIER_RE =
  /^cubic-bezier\(\s*-?\d*\.?\d+\s*,\s*-?\d*\.?\d+\s*,\s*-?\d*\.?\d+\s*,\s*-?\d*\.?\d+\s*\)$/;
const STEPS_RE =
  /^steps\(\s*\d{1,3}\s*(,\s*(jump-start|jump-end|jump-none|jump-both|start|end))?\s*\)$/;

/** Resolve an easing id or expression to a CSS timing function (unknown values → fallback). */
export function resolveEasing(
  value: string | undefined,
  fallback: string = EASINGS['out-quart'],
): string {
  if (!value) return fallback;
  const v = value.trim();
  if (v in EASINGS) return EASINGS[v as EasingId];
  const spring = SPRING_RE.exec(v);
  if (spring) return springEasing(Number(spring[1]), Number(spring[2]));
  if (BEZIER_RE.test(v) || STEPS_RE.test(v)) return v;
  return fallback;
}

/** True for easings that overshoot (useful to keep scale/opacity presets sane). */
export function isOvershooting(value: string | undefined): boolean {
  return /spring|back|elastic|bounce/.test(value ?? '');
}

/** Shape of an easing as a function of progress (0..1 → 0..1, may overshoot): for drawing it. */
export function easingCurve(value: string | undefined): (x: number) => number {
  const css = resolveEasing(value);
  if (css === 'linear') return (x) => x;
  if (css === 'ease') return bezier(0.25, 0.1, 0.25, 1);
  const b = /^cubic-bezier\(([^)]+)\)$/.exec(css);
  if (b) {
    const [x1, y1, x2, y2] = b[1].split(',').map(Number);
    return bezier(x1, y1, x2, y2);
  }
  const l = /^linear\(([^)]+)\)$/.exec(css);
  if (l) {
    const ys = l[1].split(',').map(Number);
    return (x) => {
      const p = Math.max(0, Math.min(1, x)) * (ys.length - 1);
      const i = Math.min(ys.length - 2, Math.floor(p));
      return ys[i] + (ys[i + 1] - ys[i]) * (p - i);
    };
  }
  return (x) => x;
}

function bezier(x1: number, y1: number, x2: number, y2: number): (x: number) => number {
  const at = (a: number, b: number, t: number) =>
    3 * a * t * (1 - t) * (1 - t) + 3 * b * t * t * (1 - t) + t * t * t;
  return (x) => {
    // x(t) is monotonic on [0, 1]: bisection is plenty for drawing
    let lo = 0;
    let hi = 1;
    for (let i = 0; i < 30; i++) {
      const mid = (lo + hi) / 2;
      if (at(x1, x2, mid) < x) lo = mid;
      else hi = mid;
    }
    return at(y1, y2, (lo + hi) / 2);
  };
}

/** Names (en, uk, pl, ro, es, ru) and groups of the curves, as the editor lists them. */
export const EASING_INFO: Record<EasingId, { group: EasingGroup; label: Label }> = {
  linear: {
    group: 'basic',
    label: ['Linear', 'Рівномірно', 'Liniowo', 'Liniar', 'Lineal', 'Равномерно'],
  },
  ease: { group: 'basic', label: ['Gentle', 'М’яко', 'Łagodnie', 'Lin', 'Suave', 'Мягко'] },
  'ease-in': {
    group: 'basic',
    label: ['Speed up', 'Розгін', 'Przyspieszenie', 'Accelerare', 'Acelerar', 'Разгон'],
  },
  'ease-out': {
    group: 'basic',
    label: ['Slow down', 'Гальмування', 'Hamowanie', 'Încetinire', 'Frenar', 'Торможение'],
  },
  'ease-in-out': {
    group: 'basic',
    label: [
      'Speed up and slow down',
      'Розгін і гальмування',
      'Przyspieszenie i hamowanie',
      'Accelerare și încetinire',
      'Acelerar y frenar',
      'Разгон и торможение',
    ],
  },
  'out-quart': {
    group: 'smooth',
    label: [
      'Smooth stop',
      'Плавна зупинка',
      'Płynne zatrzymanie',
      'Oprire lină',
      'Parada suave',
      'Плавная остановка',
    ],
  },
  'out-expo': {
    group: 'smooth',
    label: [
      'Swift, long settle',
      'Стрімко, довга зупинка',
      'Szybko, długie wyhamowanie',
      'Rapid, oprire lungă',
      'Rápido, parada larga',
      'Стремительно, долгая остановка',
    ],
  },
  snappy: {
    group: 'smooth',
    label: ['Snappy', 'Клацання', 'Zatrzask', 'Fulger', 'Chasquido', 'Щелчок'],
  },
  'in-quart': {
    group: 'smooth',
    label: [
      'Smooth start',
      'Плавний старт',
      'Płynny start',
      'Pornire lină',
      'Inicio suave',
      'Плавный старт',
    ],
  },
  'in-expo': {
    group: 'smooth',
    label: [
      'Sharp take-off',
      'Різкий розгін',
      'Ostry start',
      'Plecare bruscă',
      'Arranque brusco',
      'Резкий разгон',
    ],
  },
  'in-out-quart': {
    group: 'smooth',
    label: [
      'Smooth start and stop',
      'Плавно рушає і зупиняється',
      'Płynny start i stop',
      'Pornire și oprire lină',
      'Arranque y parada suaves',
      'Плавно трогается и останавливается',
    ],
  },
  'in-out-expo': {
    group: 'smooth',
    label: [
      'Fast through the middle',
      'Швидко посередині',
      'Szybko w środku',
      'Rapid la mijloc',
      'Rápido en el medio',
      'Быстро в середине',
    ],
  },
  'out-back': {
    group: 'character',
    label: [
      'Overshoot',
      'З перельотом',
      'Z przestrzeleniem',
      'Cu depășire',
      'Con rebase',
      'С перелётом',
    ],
  },
  'in-back': {
    group: 'character',
    label: ['Wind-up', 'З замахом', 'Z zamachem', 'Cu avânt', 'Con impulso', 'С замахом'],
  },
  'in-out-back': {
    group: 'character',
    label: [
      'Wind-up and overshoot',
      'Замах і переліт',
      'Zamach i przestrzelenie',
      'Avânt și depășire',
      'Impulso y rebase',
      'Замах и перелёт',
    ],
  },
  spring: {
    group: 'physics',
    label: ['Spring', 'Пружина', 'Sprężyna', 'Arc', 'Muelle', 'Пружина'],
  },
  'spring-soft': {
    group: 'physics',
    label: [
      'Soft spring',
      'М’яка пружина',
      'Miękka sprężyna',
      'Arc moale',
      'Muelle suave',
      'Мягкая пружина',
    ],
  },
  'spring-bouncy': {
    group: 'physics',
    label: [
      'Lively spring',
      'Жвава пружина',
      'Żywa sprężyna',
      'Arc vioi',
      'Muelle vivo',
      'Резвая пружина',
    ],
  },
  bounce: {
    group: 'physics',
    label: ['Bounce', 'Відскок', 'Odbicie', 'Săritură', 'Rebote', 'Отскок'],
  },
  elastic: {
    group: 'physics',
    label: ['Elastic', 'Гумка', 'Gumka', 'Elastic', 'Elástico', 'Резинка'],
  },
};

export const EASING_GROUPS = ['basic', 'smooth', 'character', 'physics'] as const;
export type EasingGroup = (typeof EASING_GROUPS)[number];
export const EASING_GROUP_LABELS: Record<EasingGroup, Label> = {
  basic: ['Basic', 'Базові', 'Podstawowe', 'De bază', 'Básicas', 'Базовые'],
  smooth: ['Smooth', 'Плавні', 'Płynne', 'Line', 'Suaves', 'Плавные'],
  character: [
    'With character',
    'З характером',
    'Z charakterem',
    'Cu personalitate',
    'Con carácter',
    'С характером',
  ],
  physics: [
    'Springs and bounces',
    'Пружини та відскоки',
    'Sprężyny i odbicia',
    'Arcuri și sărituri',
    'Muelles y rebotes',
    'Пружины и отскоки',
  ],
};
